import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

export default function useInactivityTimeout() {
  const { logout } = useAuth0();
  const timerRef = useRef(null);
  const agencyAssessments = useSelector((state) => state.assessments?.items);

  const hasOnGoingAssessment = useCallback(() => {
    if (!Array.isArray(agencyAssessments)) return false;
    // Treat anything that hasn't reached a terminal state as "ongoing".
    const terminal = new Set(['completed', 'discarded', 'archived']);
    return agencyAssessments.some(
      (a) => !terminal.has((a?.status || '').toLowerCase())
    );
  }, [agencyAssessments]);

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!hasOnGoingAssessment()) {
      timerRef.current = setTimeout(handleLogout, INACTIVITY_TIMEOUT_MS);
    }
  }, [hasOnGoingAssessment, handleLogout]);

  useEffect(() => {
    resetTimer();

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true })
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [resetTimer]);
}
